import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { Teacher, isTeacher } from '../../../utils/firebase';
import {
  collection,
  query,
  onSnapshot,
  getDocs,
  writeBatch,
  doc,
  serverTimestamp,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import styles from './ManitoManagement.module.css';

interface StudentRow {
  uid: string;
  name: string;
  number: number;
}

interface TargetInfo {
  targetUid: string;
  targetName: string;
  targetNumber: number;
}

interface AssignmentRow extends StudentRow, TargetInfo {}

function shuffleIds(ids: string[]): string[] {
  const a = [...ids];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildCircularTargets(studentUids: string[]): Map<string, string> {
  const map = new Map<string, string>();
  if (studentUids.length < 2) return map;
  const shuffled = shuffleIds(studentUids);
  for (let i = 0; i < shuffled.length; i++) {
    map.set(shuffled[i], shuffled[(i + 1) % shuffled.length]);
  }
  return map;
}

/** Firestore에 번호가 숫자/문자열 등으로 저장된 경우 모두 처리 */
function toStudentNumber(data: Record<string, unknown>): number {
  const raw = data.number ?? data.studentNumber;
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  if (typeof raw === 'string') {
    const p = parseInt(raw, 10);
    return Number.isNaN(p) ? 0 : p;
  }
  return 0;
}

const ManitoManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const teacher = currentUser as Teacher;
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [manitoMap, setManitoMap] = useState<Map<string, TargetInfo>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmRematch, setConfirmRematch] = useState(false);

  const canUse = isTeacher(currentUser) && teacher?.homeroomClass && teacher.classId && teacher.schoolId;

  const basePath = useMemo(() => {
    if (!teacher?.schoolId || !teacher?.classId) return '';
    return `schools/${teacher.schoolId}/classes/${teacher.classId}`;
  }, [teacher?.schoolId, teacher?.classId]);

  useEffect(() => {
    if (!canUse || !basePath) {
      setLoading(false);
      return;
    }
    const studentsRef = collection(db, `${basePath}/students`);
    const unsub = onSnapshot(query(studentsRef), (snapshot) => {
      const rows: StudentRow[] = snapshot.docs
        .map((d) => {
          const data = d.data();
          if (data.approvalStatus === 'rejected') return null;
          return {
            uid: d.id,
            name: (data.name as string) || '이름 없음',
            number: toStudentNumber(data as Record<string, unknown>),
          };
        })
        .filter((r): r is StudentRow => r !== null)
        .sort((a, b) => a.number - b.number || a.name.localeCompare(b.name));
      setStudents(rows);
      setLoading(false);
    });
    return () => unsub();
  }, [canUse, basePath]);

  useEffect(() => {
    if (!basePath) return;
    const manitoRef = collection(db, `${basePath}/manitoAssignments`);
    const unsub = onSnapshot(query(manitoRef), (snapshot) => {
      const m = new Map<string, TargetInfo>();
      snapshot.docs.forEach((d) => {
        const data = d.data();
        m.set(d.id, {
          targetUid: data.targetStudentId || '',
          targetName: data.targetName || '',
          targetNumber: toStudentNumber({ number: data.targetNumber } as Record<string, unknown>),
        });
      });
      setManitoMap(m);
    });
    return () => unsub();
  }, [basePath]);

  const assignments: AssignmentRow[] = useMemo(
    () =>
      students.map((s) => {
        const t = manitoMap.get(s.uid);
        return {
          ...s,
          targetUid: t?.targetUid || '',
          targetName: t?.targetName || '',
          targetNumber: t?.targetNumber ?? 0,
        };
      }),
    [students, manitoMap]
  );

  const runRematch = useCallback(async () => {
    if (!basePath || students.length < 2) {
      setError('마니또 매칭은 학급에 승인된 학생이 2명 이상일 때 가능합니다.');
      return;
    }
    setError(null);
    setSuccess(null);
    setSaving(true);
    try {
      const uids = students.map((s) => s.uid);
      const targets = buildCircularTargets(uids);
      const byUid = new Map(students.map((s) => [s.uid, s]));

      const colRef = collection(db, `${basePath}/manitoAssignments`);
      const existing = await getDocs(query(colRef));
      for (const d of existing.docs) {
        await deleteDoc(d.ref);
      }

      let batch = writeBatch(db);
      let n = 0;
      for (const [giverUid, receiverUid] of targets) {
        const receiver = byUid.get(receiverUid);
        if (!receiver) continue;
        const ref = doc(db, `${basePath}/manitoAssignments`, giverUid);
        batch.set(ref, {
          targetStudentId: receiverUid,
          targetName: receiver.name,
          targetNumber: receiver.number,
          updatedAt: serverTimestamp(),
        });
        n++;
        if (n >= 400) {
          await batch.commit();
          batch = writeBatch(db);
          n = 0;
        }
      }
      if (n > 0) await batch.commit();
      setSuccess('마니또 매칭이 저장되었습니다. 학생 화면에서 각자 배정된 친구를 확인할 수 있습니다.');
      setConfirmRematch(false);
    } catch (e) {
      console.error('마니또 저장 오류:', e);
      setError('저장 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSaving(false);
    }
  }, [basePath, students]);

  const handleClearAll = useCallback(async () => {
    if (!basePath) return;
    setError(null);
    setSuccess(null);
    setSaving(true);
    try {
      const colRef = collection(db, `${basePath}/manitoAssignments`);
      const existing = await getDocs(query(colRef));
      for (const d of existing.docs) {
        await deleteDoc(d.ref);
      }
      setSuccess('모든 마니또 매칭을 삭제했습니다.');
    } catch (e) {
      console.error(e);
      setError('삭제 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  }, [basePath]);

  if (!isTeacher(currentUser)) {
    return (
      <div style={{ padding: '16px 12px', color: 'var(--Color-text-normal)' }}>
        교사만 이용할 수 있는 페이지입니다.
      </div>
    );
  }

  if (!teacher.homeroomClass || !teacher.classId) {
    return (
      <div style={{ padding: '16px 12px', color: 'var(--Color-text-normal)' }}>
        담임 학급이 설정된 교사 계정에서만 마니또를 관리할 수 있습니다.
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        padding: '12px 12px',
        fontFamily: 'var(--font-family-primary)',
      }}
    >
      <h1
        style={{
          fontSize: '24px',
          fontWeight: 600,
          color: 'var(--Color-text-strong)',
          margin: '0 0 8px',
          letterSpacing: '-0.02em',
        }}
      >
        마니또 매칭
      </h1>
      <p style={{ margin: '0 0 24px', fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: 1.5 }}>
        전원이 무작위로 한 명씩 배정됩니다. 각 학생은 <strong>자신이 챙겨 줄 마니또</strong> 한 명만 앱에서 볼 수
        있습니다. 다시 뽑으면 이전 매칭은 모두 바뀝니다.
      </p>

      {error && (
        <div
          role="alert"
          style={{
            marginBottom: '16px',
            padding: '12px 16px',
            borderRadius: '8px',
            backgroundColor: 'var(--Color-fill-hover)',
            color: 'var(--Color-text-strong)',
            fontSize: '14px',
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          role="status"
          style={{
            marginBottom: '16px',
            padding: '12px 16px',
            borderRadius: '8px',
            backgroundColor: 'rgba(76, 175, 80, 0.12)',
            color: 'var(--Color-text-strong)',
            fontSize: '14px',
          }}
        >
          {success}
        </div>
      )}

      <Card width="100%" padding="16px 12px" borderRadius="16px" background="var(--Color-background-normal)">
        {loading ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--Color-text-normal)' }}>
            불러오는 중…
          </div>
        ) : students.length < 2 ? (
          <div style={{ color: 'var(--Color-text-normal)', fontSize: '14px' }}>
            매칭 가능한 학생이 2명 미만입니다. 학급 관리에서 학생을 초대·승인해 주세요.
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--gap-level-3)',
                marginBottom: '20px',
                alignItems: 'center',
              }}
            >
              {!confirmRematch ? (
                <Button variant="primary" size="medium" onClick={() => setConfirmRematch(true)} disabled={saving}>
                  마니또 다시 뽑기
                </Button>
              ) : (
                <>
                  <span style={{ fontSize: '14px', color: 'var(--Color-text-normal)' }}>
                    기존 매칭이 사라지고 새로 섞입니다. 진행할까요?
                  </span>
                  <Button variant="primary" size="medium" onClick={runRematch} disabled={saving}>
                    {saving ? '저장 중…' : '확인'}
                  </Button>
                  <Button variant="outline" size="medium" onClick={() => setConfirmRematch(false)} disabled={saving}>
                    취소
                  </Button>
                </>
              )}
              <Button
                variant="line"
                size="medium"
                onClick={handleClearAll}
                disabled={saving || assignments.every((a) => !a.targetName)}
              >
                전체 삭제
              </Button>
            </div>

            <div className={styles.assignmentsGrid} role="list">
              {assignments.map((row) => (
                <Card
                  key={row.uid}
                  role="listitem"
                  width="100%"
                  padding="16px 18px"
                  borderRadius="16px"
                  background="var(--Color-background-normal)"
                  gap="0"
                  flexDirection="column"
                  alignItems="stretch"
                  style={{ boxSizing: 'border-box', minWidth: 0 }}
                >
                  <div className={styles.matchCardBody}>
                    <div className={styles.matchFrom}>
                      <span className={styles.matchLabel}>학생</span>
                      <span className={styles.matchName}>
                        {row.number > 0 ? `${row.number}번 ` : ''}
                        {row.name}
                      </span>
                    </div>
                    <div className={styles.matchTo}>
                      <span className={styles.matchLabel}>챙겨 줄 마니또</span>
                      {row.targetName ? (
                        <span className={styles.matchTarget}>
                          {row.targetNumber > 0 ? `${row.targetNumber}번 ` : ''}
                          {row.targetName}
                        </span>
                      ) : (
                        <span className={styles.matchPending}>미배정</span>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default ManitoManagement;
